/**
 * Client Supabase dédié à l'outil Mapping RH.
 * --------------------------------------------------------------------
 * Le projet mapping-rh utilise une instance Supabase distincte de celle
 * de recruteurs.io (table `leads` + edge function `test-friction` +
 * compte OpenAI séparé). On évite donc le client Supabase global et on
 * isole les credentials dans des variables d'environnement dédiées :
 *
 *   VITE_MAPPING_SUPABASE_URL       = https://fxutlubjfhdfhpiqmisk.supabase.co
 *   VITE_MAPPING_SUPABASE_ANON_KEY  = clé anon publique du projet mapping
 *
 * Ces variables sont à configurer dans Netlify (Site config > Env
 * variables) et dans `.env` local (ignoré par git).
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_MAPPING_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_MAPPING_SUPABASE_ANON_KEY as
  | string
  | undefined;

let client: SupabaseClient | null = null;

if (url && anonKey) {
  client = createClient(url, anonKey, {
    auth: { persistSession: false },
  });
} else if (import.meta.env.DEV) {
  // Avertit en dev local si les vars sont absentes — pas en prod, pour
  // éviter de polluer la console des utilisateurs.
  console.warn(
    '[mappingClient] VITE_MAPPING_SUPABASE_URL ou VITE_MAPPING_SUPABASE_ANON_KEY manquants. ' +
      "L'outil de Mapping RH ne pourra pas appeler l'edge function ni enregistrer de lead.",
  );
}

export const mappingSupabase = client;

/**
 * Renvoie l'URL complète d'une edge function du projet mapping.
 * Ex : edgeFunctionUrl('test-friction')
 *      → https://fxutlubjfhdfhpiqmisk.supabase.co/functions/v1/test-friction
 */
export function edgeFunctionUrl(name: string): string | null {
  if (!url) return null;
  return `${url.replace(/\/$/, '')}/functions/v1/${name}`;
}

/** Indique si le client mapping est prêt (env vars présentes au build). */
export const isMappingClientReady = !!client;

export const mappingAnonKey = anonKey;
